<template>
  <div class="company-followup-page" dir="rtl">
    <h1 class="page-title">פולואפ מעסיקים — "מצאת עבודה?"</h1>
    <p class="page-subtitle">
      שליחת שיחת פולואפ בוואטסאפ למועמדים שהוגשו בעבר למעסיק הנבחר, לבדוק אם מצאו עבודה.
    </p>

    <!-- Company picker -->
    <div class="picker-row">
      <div class="picker-search">
        <SearchCompanies ref="search_companies" class="ltr" @select="onCompanySelected" />
      </div>
      <div v-if="selectedCompany" class="company-chip">
        <span class="company-chip-name">{{ selectedCompany.name }}</span>
        <button class="company-chip-clear" title="ניקוי בחירת מעסיק" @click="clearCompany">×</button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="is_loading" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <template v-else-if="preview">
      <!-- Kill-switch warning -->
      <div v-if="preview.enabled === false" class="flag-banner">
        הפיצ׳ר כבוי (COMPANY_FOLLOWUP_ENABLED) — שליחה חסומה
      </div>

      <!-- Counts summary -->
      <div class="counts-summary">
        <div class="counts-primary">
          נמצאו {{ counts.selected }} מועמדים מתאימים
          (מתוך {{ counts.base_applicants }} שנשלחו למעסיק בשנה האחרונה)
        </div>
        <div class="counts-secondary">
          לא נכללו:
          {{ counts.excluded_recently_sent }} נשלחו לאחרונה ·
          {{ counts.excluded_already_followed }} כבר נעשה פולואפ ·
          {{ counts.excluded_vesting }} עובדים שם ·
          {{ counts.excluded_gates }} חסומים בשערים
        </div>
      </div>

      <!-- Table -->
      <table v-if="candidates.length" class="followup-table">
        <thead>
          <tr>
            <th class="col-check">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
                title="בחירת הכל / ניקוי"
              />
            </th>
            <th>מזהה מועמד</th>
            <th>שם</th>
            <th>טלפון</th>
            <th>משרות</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in candidates" :key="c.applicant_id" :class="{ 'row-selected': isSelected(c.applicant_id) }">
            <td class="col-check">
              <input
                type="checkbox"
                :checked="isSelected(c.applicant_id)"
                @change="toggleRow(c.applicant_id)"
              />
            </td>
            <td>
              <router-link
                class="applicant-link"
                :to="{ name: 'admin-applicants', query: { show_id: c.applicant_id } }"
              >{{ c.applicant_id }}</router-link>
            </td>
            <td>{{ c.full_name || '—' }}</td>
            <td>{{ c.phone_masked || '—' }}</td>
            <td>{{ (c.position_titles && c.position_titles.length) ? c.position_titles.join(', ') : '—' }}</td>
          </tr>
        </tbody>
      </table>

      <p v-else class="no-results">לא נמצאו מועמדים מתאימים למעסיק זה</p>
    </template>

    <p v-else class="hint">בחרי מעסיק כדי לראות מועמדים מתאימים לפולואפ.</p>

    <!-- Bottom action bar -->
    <div v-if="preview && candidates.length" class="action-bar">
      <button
        class="send-btn"
        :disabled="selectedCount === 0 || preview.enabled === false || is_sending"
        @click="openSendConfirm"
      >{{ is_sending ? '...שולח' : 'שליחת וואטסאפ (' + selectedCount + ')' }}</button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import SearchCompanies from '@/components/SearchCompanies'

export default {
  name: 'company-followup-page',
  components: {
    SearchCompanies
  },
  data () {
    return {
      selectedCompany: null,
      preview: null,
      selected_ids: [],
      is_loading: false,
      is_sending: false,
      confirm_open: false
    }
  },
  computed: {
    candidates () {
      return this.preview && this.preview.candidates ? this.preview.candidates : []
    },
    counts () {
      // Always start from a full set of zero defaults, then overlay whatever the
      // backend actually returned. A company with no positions/offers yields a
      // partial counts hash; without this merge the missing summary numbers would
      // render as blanks.
      const defaults = {
        company_positions: 0,
        company_offers: 0,
        base_applicants: 0,
        excluded_recently_sent: 0,
        excluded_already_followed: 0,
        excluded_vesting: 0,
        excluded_gates: 0,
        selected: 0
      }
      return Object.assign({}, defaults, (this.preview && this.preview.counts) || {})
    },
    selectedCount () {
      return this.selected_ids.length
    },
    allSelected () {
      return this.candidates.length > 0 && this.selected_ids.length === this.candidates.length
    }
  },
  methods: {
    ...mapMutations('modals', ['create', 'removeModalById']),
    onCompanySelected (company) {
      if (!company || company.resource_id == undefined) {
        return
      }
      this.selectedCompany = company
      this.selected_ids = []
      this.loadPreview()
    },
    clearCompany () {
      this.selectedCompany = null
      this.preview = null
      this.selected_ids = []
      if (this.$refs.search_companies && this.$refs.search_companies.clearText) {
        this.$refs.search_companies.clearText()
      }
    },
    loadPreview () {
      if (!this.selectedCompany) {
        return
      }
      this.is_loading = true
      this.$network_utils
        .getCompanyFollowupPreview({ company_id: this.selectedCompany.resource_id, limit: 100 })
        .then(res => {
          this.preview = res || null
          // Keep only still-present selections after a reload.
          const availableIds = this.candidates.map(c => c.applicant_id)
          this.selected_ids = this.selected_ids.filter(id => availableIds.includes(id))
        })
        .catch(error => {
          const slug = error && error.response && error.response.data && error.response.data.error
          if (slug === 'company_not_found') {
            this.$utils.emit('show_toast', { text: 'המעסיק לא נמצא', type: 'error' })
            this.clearCompany()
          } else {
            this.$utils.emit('show_toast', { text: 'שגיאה בטעינת מועמדים לפולואפ', type: 'error' })
          }
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    isSelected (id) {
      return this.selected_ids.includes(id)
    },
    toggleRow (id) {
      const idx = this.selected_ids.indexOf(id)
      if (idx === -1) {
        this.selected_ids.push(id)
      } else {
        this.selected_ids.splice(idx, 1)
      }
    },
    toggleSelectAll () {
      if (this.allSelected) {
        this.selected_ids = []
      } else {
        this.selected_ids = this.candidates.map(c => c.applicant_id)
      }
    },
    openSendConfirm () {
      // confirm_open guards against a rapid double-click stacking two identical
      // confirmation modals. It is cleared in BOTH modal handlers below.
      if (this.selectedCount === 0 || !this.selectedCompany || this.is_sending || this.confirm_open) {
        return
      }
      if (this.preview && this.preview.enabled === false) {
        return
      }
      const count = this.selectedCount
      const companyName = this.selectedCompany.name
      this.confirm_open = true
      this.create({
        designator: 'company-followup-confirm-modal',
        component_name: 'ModalConfirmation',
        modal_title: 'שליחת פולואפ בוואטסאפ',
        modal_width: '360px',
        is_center_modal: true,
        props: {
          message: 'האם תרצי לשלוח פנייה בוואטסאפ ל־' + count + ' מועמדים של ' + companyName + '?',
          onConfirmation: (id) => {
            this.confirm_open = false
            this.removeModalById(id)
            this.doSend()
          },
          onReject: (id) => {
            this.confirm_open = false
            this.removeModalById(id)
          }
        }
      })
    },
    doSend () {
      if (this.is_sending || !this.selectedCompany) {
        return
      }
      const ids = this.selected_ids.slice()
      if (!ids.length) {
        return
      }
      this.is_sending = true
      this.$network_utils
        .sendCompanyFollowup({ company_id: this.selectedCompany.resource_id, applicant_ids: ids })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'נשלחו ' + ids.length + ' פניות לתור השליחה' })
          // The worker is async — the backend excludes sent candidates only after
          // it runs, so optimistically drop the sent rows now to avoid a race,
          // then reload for the authoritative list.
          if (this.preview && this.preview.candidates) {
            this.preview.candidates = this.preview.candidates.filter(c => !ids.includes(c.applicant_id))
          }
          this.selected_ids = []
          this.loadPreview()
        })
        .catch(error => {
          const slug = error && error.response && error.response.data && error.response.data.error
          this.$utils.emit('show_toast', { text: this.sendErrorMessage(slug), type: 'error' })
          if (slug === 'company_not_found') {
            this.clearCompany()
          }
        })
        .finally(() => {
          this.is_sending = false
        })
    },
    sendErrorMessage (slug) {
      switch (slug) {
        case 'company_followup_disabled':
          return 'הפיצ׳ר כבוי (COMPANY_FOLLOWUP_ENABLED) — שליחה חסומה'
        case 'too_many_applicants':
          return 'ניתן לשלוח עד 100 מועמדים בכל פעם'
        case 'applicant_ids_required':
          return 'לא נבחרו מועמדים לשליחה'
        case 'company_not_found':
          return 'המעסיק לא נמצא'
        default:
          return 'שגיאה בשליחת הפולואפ — נסי שוב'
      }
    }
  }
}
</script>

<style scoped>
.company-followup-page {
  padding: 20px;
  text-align: right;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #2c3e50;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 18px;
}
.picker-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.picker-search {
  min-width: 260px;
}
.company-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e2e3ff;
  color: #3b3f99;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 600;
}
.company-chip-clear {
  background: none;
  border: none;
  color: #3b3f99;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.flag-banner {
  background: #fdecea;
  border: 1px solid #f5b7b1;
  color: #c0392b;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 16px;
}
.counts-summary {
  margin-bottom: 12px;
}
.counts-primary {
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
}
.counts-secondary {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.followup-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.followup-table th,
.followup-table td {
  padding: 10px 12px;
  font-size: 14px;
  text-align: right;
}
.followup-table tr {
  border-bottom: 2px solid #cbd5e0;
}
.followup-table th {
  background: #f1f5f9;
  font-weight: 700;
  color: #2c3e50;
}
.followup-table tr.row-selected {
  background: #f5f6fe;
}
.col-check {
  width: 40px;
  text-align: center;
}
.applicant-link {
  color: #3273dc;
  text-decoration: underline;
}
.no-results {
  padding: 30px;
  text-align: center;
  color: #7f8c8d;
}
.hint {
  padding: 30px 0;
  color: #7f8c8d;
}
.action-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-start;
  padding: 16px 0;
  margin-top: 16px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0));
}
.send-btn {
  background: #25a244;
  border: 1px solid #25a244;
  color: #fff;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(37, 162, 68, 0.35);
  transition: all 0.15s ease;
}
.send-btn:hover:not(:disabled) {
  background: #1f8c3a;
  border-color: #1f8c3a;
}
.send-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
